import { useCallback, useEffect, useState } from "react";
import { fetchWalletDetail, type ApiErrorInfo } from "@/lib/api";
import { isNonZeroBalance, readWalletBalances } from "@/lib/wallet-balances";
import { TRIO_2OF3_SIGNING_TOPOLOGY } from "@/components/data-display/signing-topology-chip";
import type { WalletDetail } from "@/types";
import { formatUsd } from "@/lib/format-money";
import { UNKNOWN_VALUE_LABEL } from "@/features/valuation/valuation";
import { walletDisplayName } from "@/lib/wallet-display-name";

/**
 * ALL-4862 (wallet-page PM review): the gateway does not yet return a
 * `routes`/capabilities array on `GET /v1/wallets/{id}`, so this fallback
 * fires for every wallet today. It previously guessed "Send" support from a
 * substring match on `walletProfileId` and labeled Swap/Stake "Not available
 * in sandbox" — wording that implies a temporary environment restriction
 * rather than the truth: those routes are not built yet anywhere. Send now
 * prefers the real `signingTopology` field (GatewayWalletResponse) when the
 * backend sends it, only falling back to the profile-id heuristic for
 * wallets that predate that field. Swap/Stake use customer-facing availability
 * copy. A real gateway capabilities contract can replace this fallback.
 */

interface UseWalletDetailResult {
  detail: WalletDetail | null;
  loading: boolean;
  error: string | null;
  /** Structured failure (status class, support reference, capability) for the page to branch on. */
  failure: ApiErrorInfo | null;
  /** True only when the wallet is known to be absent. */
  notFound: boolean;
  source: "api";
  refresh: () => void;
  /** Reload without a spinner or an error state (see load). */
  refreshSilently: () => void;
}

/**
 * ALL-4915: `walletProfileId.includes("signing")` also matches
 * "sandbox-no-signing" — a *disabled*-signing profile — as if it were a
 * signing-enabled one, mislabeling its provider as "Mesh (VaultCore)" and
 * offering a working-looking "Send" route it can't actually fulfill. A
 * profile id is signing-enabled only when it says "signing" and does not
 * also explicitly say "no-signing".
 */
function walletProfileClassification(walletProfileId: string): {
  isFireblocks: boolean;
  isSigningEnabled: boolean;
} {
  const isFireblocks = walletProfileId.includes("fireblocks");
  const isNoSigning = walletProfileId.includes("no-signing");
  const isSigningEnabled = !isNoSigning && walletProfileId.includes("signing");
  return { isFireblocks, isSigningEnabled };
}

function valuationRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

/** Display the measured gateway total, with no customer-facing environment/status tag. */
function walletTotalStat(rawValuation: unknown): { value: string; label: string; hint: string } {
  const valuation = valuationRecord(rawValuation);
  const valued = valuation && typeof valuation.valuedTotal === "string" ? valuation.valuedTotal : null;
  const reference = valuation && typeof valuation.referenceTotal === "string" ? valuation.referenceTotal : null;
  const total = valued ?? reference;
  if (total !== null) {
    return { value: formatUsd(total), label: "Total Balance (USD)", hint: "" };
  }
  return { value: UNKNOWN_VALUE_LABEL, label: "Total Balance (USD)", hint: "USD pricing not available yet" };
}

/**
 * A balance row's amount, with its symbol once.
 *
 * A gateway row that carries atomic units but no decimal amount is *unknown*,
 * not zero: printing "0 ETH" for a wallet whose `balanceAtomic` is non-zero
 * states it holds no ether, which is a fabricated balance (ALL-5553). Unknown
 * renders as the shared dash, the same one the USD cell uses.
 */
function balanceAmount(balance: Record<string, unknown>, symbol: string): string {
  const raw = balance.amount ?? balance.balance;
  if (typeof raw !== "string" || raw.trim() === "") {
    return UNKNOWN_VALUE_LABEL;
  }
  return raw.endsWith(` ${symbol}`) ? raw : `${raw} ${symbol}`;
}

/** Only the gateway valuation supplies a balance row's USD cell. */
function balanceUsd(balance: Record<string, unknown>): string {
  const valuation = valuationRecord(balance.valuation);
  const value = valuation && typeof valuation.value === "string" ? valuation.value : null;
  if (value !== null) {
    return formatUsd(value);
  }
  return UNKNOWN_VALUE_LABEL;
}

export function mapApiWalletDetail(raw: Record<string, unknown>): WalletDetail {
  // GatewayWalletDetailResponse wraps wallet fields inside a nested "wallet" object.
  // Unwrap it so field reads (walletId, chain, …) resolve correctly.
  const w = (raw.wallet && typeof raw.wallet === "object" && !Array.isArray(raw.wallet))
    ? raw.wallet as Record<string, unknown>
    : raw;

  const id = String(w.walletId ?? w.id ?? "");
  // ALL-5795: a machine-made alias gets a readable name; the address and id stay on the page.
  const name = walletDisplayName({
    alias: [w.displayName, w.name, w.accountAlias, w.label].find((v) => typeof v === "string" && v.trim()) as string | undefined,
    walletId: id,
    network: typeof w.network === "string" ? w.network : null,
    chain: typeof w.chain === "string" ? w.chain : null,
    address: typeof w.address === "string" ? w.address : null,
  }).name;
  const chain = String(w.chain ?? "—");
  const network = String(w.network ?? "—");
  const address = String(w.address ?? "—");
  const walletProfileId = String(w.walletProfileId ?? "—");
  const environment = String(w.environment ?? "—");
  // ALL-4884: see mapApiWallet in use-wallets.ts.
  const signingTopology = typeof w.signingTopology === "string" ? w.signingTopology : null;
  const createdAt = w.createdAt ? new Date(String(w.createdAt)).toLocaleString() : "—";

  const stats = [
    // Only the gateway valuation contract may supply a customer-facing USD total.
    walletTotalStat(raw.valuation),
    { value: chain, label: "Chain" },
    { value: network, label: "Network" },
    { value: environment.charAt(0).toUpperCase() + environment.slice(1), label: "Environment", tone: "success" as const },
  ];

  // GatewayBalanceSnapshot uses "asset" / "balance"; map to UI field names.
  const balances = Array.isArray(raw.balances)
    ? (raw.balances as Record<string, unknown>[]).filter(isNonZeroBalance).map(b => {
        const symbol = String(b.symbol ?? b.asset ?? "");
        return {
          symbol,
          name: String(b.name ?? symbol),
          amount: balanceAmount(b, symbol),
          // PriceKit valuation when present; otherwise "—", never a fabricated "$0" next to a real balance.
          usd: balanceUsd(b),
          iconLabel: String(b.iconLabel ?? symbol),
        };
      })
    : [];

  const { isFireblocks, isSigningEnabled } = walletProfileClassification(walletProfileId);
  // Real signal first: a wallet actually bound to the Trio 2-of-3 signer
  // topology can really sign and broadcast. Only guess from the profile id
  // when the backend hasn't sent signingTopology for this wallet yet.
  const hasBoundSigner = signingTopology === TRIO_2OF3_SIGNING_TOPOLOGY;
  const sendSupported = hasBoundSigner || isFireblocks || isSigningEnabled;
  const sendDetail = hasBoundSigner
    ? "Sign & co-sign via Trio (Mesh)"
    : isFireblocks
      ? "MPC signing via Fireblocks"
      : isSigningEnabled
        ? "Sign & broadcast via Mesh"
        : "Requires a paired signer device";

  const routes = Array.isArray(raw.routes)
    ? (raw.routes as Record<string, unknown>[]).map(r => ({
        title: String(r.title ?? ""),
        supported: r.supported === true,
        detail: String(r.detail ?? "").includes("product-design/specs/")
          ? "Not available yet"
          : String(r.detail ?? ""),
      }))
    : [
        { title: "Receive", supported: true, detail: "Deposit to wallet address" },
        { title: "Send", supported: sendSupported, detail: sendDetail },
        { title: "Swap", supported: false, detail: "Not available yet" },
        { title: "Stake", supported: false, detail: "Not available yet" },
      ];

  const recentTransfers = Array.isArray(raw.recentTransfers)
    ? (raw.recentTransfers as Record<string, unknown>[]).map(t => ({
        id: String(t.id ?? ""),
        direction: String(t.direction ?? "sent") as "sent" | "received",
        asset: String(t.asset ?? ""),
        amount: String(t.amount ?? "0"),
        status: String(t.status ?? ""),
        statusVariant: String(t.statusVariant ?? "neutral") as "success" | "warning" | "danger" | "pending" | "neutral" | "info",
        time: String(t.time ?? ""),
      }))
    : [];

  const properties = Array.isArray(raw.properties)
    ? (raw.properties as Record<string, unknown>[]).map(p => ({ k: String(p.k ?? p.key ?? ""), v: String(p.v ?? p.value ?? "") }))
    : [
        { k: "Wallet ID", v: id },
        { k: "Address", v: address },
        { k: "Chain", v: chain },
        { k: "Network", v: network },
        { k: "Provider", v: isFireblocks ? "Fireblocks" : isSigningEnabled ? "Mesh (VaultCore)" : "—" },
        { k: "Wallet Profile", v: walletProfileId },
        { k: "Environment", v: environment },
        { k: "Created", v: createdAt },
      ];

  const evidence = Array.isArray(raw.evidence)
    ? (raw.evidence as Record<string, unknown>[]).map(e => ({ id: String(e.id ?? ""), label: String(e.label ?? e.id ?? "") }))
    : [{ id: "ev-creation", label: "Wallet creation audit record" }];

  return {
    id, name, stats, balances, routes, recentTransfers,
    properties, evidence,
    hasRecovery: raw.hasRecovery === true,
    signingTopology,
  };
}

export function useWalletDetail(id: string): UseWalletDetailResult {
  const [detail, setDetail] = useState<WalletDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<ApiErrorInfo | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [source, setSource] = useState<"api">("api");

  const load = useCallback(async (silent = false) => {
    // A silent reload (the incoming-funds poll noticed a change) refreshes balances in place: no
    // spinner, so the page and the activity list the customer is reading do not unmount.
    if (!silent) {
      setLoading(true);
      setError(null);
      setFailure(null);
      setNotFound(false);
    }
    try {
      const result = await fetchWalletDetail(id);
      if (result.wallet) {
        const balanceRead = readWalletBalances(result);
        setDetail({
          ...mapApiWalletDetail(result.wallet),
          balanceStatus: balanceRead.status,
          balanceSupportReference: balanceRead.supportReference,
        });
        setSource("api");
      } else {
        if (!silent) {
          setError(result.reasonCode || "Wallet not found");
          setNotFound(result.notFound === true);
          setFailure({
            reasonCode: result.status === 403 ? "HTTP_403" : result.status === 401 ? "HTTP_401" : (result.reasonCode || "WALLET_DETAIL_FAILED"),
            supportReference: result.supportReference,
            capability: result.capability,
          });
          setDetail(null);
        }
      }
    } catch {
      if (!silent) {
        setError("Failed to load wallet details");
        setFailure({ reasonCode: "NETWORK_ERROR" });
        setDetail(null);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  return {
    detail, loading, error, failure, notFound, source,
    refresh: () => void load(),
    refreshSilently: () => void load(true),
  };
}
